import React, { forwardRef, type CSSProperties } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import classNames from "classnames";
import type { IBuilderToolbarRailOption } from "./types";
import "./builderToolbar.scss";

function builderToolbarRailOptionLabel<TId extends string>(
  option: IBuilderToolbarRailOption<TId>,
): string {
  if (option.shortcutLabel === undefined || option.shortcutLabel.length === 0) {
    return option.tooltip;
  }

  return `${option.tooltip} (${option.shortcutLabel})`;
}

interface IBuilderToolbarRailProps<TId extends string> {
  options: IBuilderToolbarRailOption<TId>[];
  activeId: TId;
  noneId: TId;
  closing: boolean;
  onSelect: (id: TId) => void;
  className?: string;
  style?: CSSProperties;
  optionClassName?: string;
  selectedOptionClassName?: string;
  role?: string;
  "aria-label"?: string;
}

function BuilderToolbarRailInner<TId extends string>(
  props: IBuilderToolbarRailProps<TId>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const {
    options,
    activeId,
    noneId,
    closing,
    onSelect,
    className,
    style,
    optionClassName,
    selectedOptionClassName,
    role,
    "aria-label": ariaLabel,
  } = props;

  return (
    <div
      ref={ref}
      className={classNames("builder-toolbar-rail", className)}
      style={style}
      role={role}
      aria-label={ariaLabel}
    >
      {options.map(option => {
        const isSelected = option.id === activeId;
        const optionLabel = builderToolbarRailOptionLabel(option);

        return (
          <OverlayTrigger
            key={option.id}
            placement="right"
            show={isSelected ? false : undefined}
            delay={{
              show: 500,
              hide: 100,
            }}
            overlay={
              <Tooltip id={`builder-toolbar-rail-option-tooltip-${option.id}`}>
                {optionLabel}
              </Tooltip>
            }
          >
            <button
              type="button"
              className={classNames("builder-toolbar-rail-option", optionClassName, {
                [selectedOptionClassName ?? "builder-toolbar-rail-option-selected"]:
                  isSelected && !closing,
              })}
              aria-label={optionLabel}
              aria-pressed={isSelected && !closing}
              onClick={() => onSelect(isSelected ? noneId : option.id)}
            >
              <FontAwesomeIcon icon={option.icon} aria-hidden="true" />
            </button>
          </OverlayTrigger>
        );
      })}
    </div>
  );
}

export const BuilderToolbarRail = forwardRef(BuilderToolbarRailInner) as <TId extends string>(
  props: IBuilderToolbarRailProps<TId> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => ReturnType<typeof BuilderToolbarRailInner>;
